import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import moment from 'moment';
import Icon from '../Icon/Icon';
import DateInput from '../DateInput/DateInput';
import TimeInput from '../TimeInput/TimeInput';
import { isMomentPropType } from '../../lib/customPropTypes';
import { timezoneToOffset } from '../../lib/dateUtils';

/**
 * `DateInput` is a a group of two input fields that displays a calendar or time picker when they get the input focus.
 *
 * Takes a [Moment](https://momentjs.com/) object as prop, and returns one [Moment](https://momentjs.com/) object including
 * with both time and date.
 *
 * If either date or time is set to `null` the other input field also set to `null`.
 *
 * @property {string} dateFormat Display format of the date ex. "DD/MM/YYYY".
 * @property {string} dateLabel Label of the date input field.
 * @property {string} datePlaceholder Placeholder in the date input field.
 * @property {boolean} isClearable Enables a clear button next to the input fields, sets both time and date to `null`.
 * @property {string} timeFormat Display format of the time ex. "HH:MM:SS".
 * @property {string} timeLabel Label of the time input field.
 * @property {string} timePlaceholder Placeholder in the time input field.
 *
 * @see Calendar, DateInput, Input, Notice, TimeInput, Timepicker.
 */
export default class DateTimeInput extends Component {

  static displayName = 'DateTimeInput';

  static propTypes = {

    className: PropTypes.string,
    dateFormat: PropTypes.string,
    dateLabel: PropTypes.string,
    datePlaceholder: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    hideIcons: PropTypes.bool,
    hour24: PropTypes.bool,
    infoMessage: PropTypes.node,
    isClearable: PropTypes.bool,
    locale: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    selected: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.instanceOf(Date),
      isMomentPropType,
    ]),
    showConfirm: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    showSeconds: PropTypes.bool,
    successMessage: PropTypes.node,
    timeFormat: PropTypes.string,
    timeLabel: PropTypes.string,
    timePlaceholder: PropTypes.string,
    utcOffset: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number]
    ),
    warningMessage: PropTypes.node,
    onTodayButtonClick: PropTypes.func,
  };

  static defaultProps = {
    dateFormat: 'L',
    locale: moment().locale(),
    utcOffset: moment.utc().utcOffset()
  };

  constructor(props) {
    super(props);
    const selected = props.selected && moment(props.selected); // props.selected might not be of type 'moment', so we re-instantiate it
    this.state = {
      selectedTime: selected,
      selectedDate: selected,
      isEmpty: false
    };
  }

  componentDidMount() {}

  componentDidUpdate(prevProps) {
    if(moment(this.props.selected).toISOString() !== moment(prevProps.selected).toISOString()){
      const selected = this.props.selected && moment(this.props.selected);
      this.setState({
        selectedTime: selected,
        selectedDate: selected,
        isEmpty: false,
      });
    }
  }

  onClear = () => {
    if (this.props.onChange && !this.props.disabled) {
      this.props.onChange(null);
    }
  }

  onDateSelect = (date) => {
    if (date) {
      const newDate = this.state.selectedDate ?
        this.state.selectedDate.clone() : moment().utcOffset(timezoneToOffset(this.props.utcOffset)).startOf('day');
      newDate.year(date.year())
        .month(date.month())
        .date(date.date());
      if (this.props.onChange) {
        this.props.onChange(newDate);
      }
    } else if (this.state.focused !== 'date') {
      this.props.onChange(null);
    } else {
      this.setState({ isEmpty: true, selectedDate: null });
    }
  }

  onTimeSelect = (time) => {
    if (time) {
      const newDate = this.state.selectedTime ?
        this.state.selectedTime.clone() : moment().utcOffset(timezoneToOffset(this.props.utcOffset)).startOf('day');
      newDate.hours(time.hours())
        .minutes(time.minutes())
        .seconds(time.seconds());
      if (this.props.onChange) {
        this.props.onChange(newDate);
      }
    } else if (this.state.focused !== 'time') {
      this.props.onChange(null);
    } else {
      this.setState({ isEmpty: true, selectedTime: null });
    }
  }

  onFocus = (element, e) => {
    this.setState({ focused: element });
  }

  onBlur = (element, e) => {
    if (this.state.isEmpty) {
      const newDate = this.props.selectedDate ?
        this.props.selectedDate.clone() : moment().utcOffset(timezoneToOffset(this.props.utcOffset)).startOf('day');
      this.props.onChange(newDate);
    }
    this.setState({ focused: null });
  }

  render() {
    let timeFormat = this.props.timeFormat;
    if (!timeFormat && this.props.hour24) {
      timeFormat = this.props.showSeconds ? 'HH:mm:ss' : 'HH:mm';
    } else if (!timeFormat && !this.props.hour24) {
      timeFormat = this.props.showSeconds ? 'hh:mm:ss A' : 'hh:mm A';
    }
    const { selectedDate, selectedTime } = this.state;
    return (
      <div className={cx('core-datetimeinput', this.props.className)}>
        {<DateInput tabIndex={0}
          {...this.props}
          dateFormat={this.props.dateFormat}
          label={this.props.dateLabel}
          onChange={this.onDateSelect.bind(this)}
          selected={selectedDate}
          placeholder={this.props.datePlaceholder}
          isClearable={false}
          showIcon={!this.props.hideIcons}
          onFocus={this.onFocus.bind(this, 'date')}
          onBlur={this.onBlur.bind(this, 'date')}/>}
        {<TimeInput tabIndex={0}
          {...this.props}
          timeFormat={timeFormat}
          label={this.props.timeLabel}
          onChange={this.onTimeSelect.bind(this)}
          placeholder={this.props.timePlaceholder}
          selected={selectedTime}
          isClearable={false}
          showIcon={!this.props.hideIcons}
          onFocus={this.onFocus.bind(this, 'time')}
          onBlur={this.onBlur.bind(this, 'time')} />}
        {this.props.isClearable ?
          <Icon name="close" title="Clear date and time" className="core-datetimeinput--clear-btn" onClick={this.onClear} />
          : null}
      </div>
    );
  }

}
